import { LayoutDashboard, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { STAGE_PURPOSE, type StageId } from "../lib/runFlow";

/**
 * NO RUN IS OPEN — and this is the screen that says so instead of guessing.
 *
 * TWO LIES LEFT THIS FILE.
 *
 *  1. THE ONE UPSTREAM. Editor and Review used to fall back to `runs[0]`, so this state was
 *     nearly unreachable: with any run in the cache, a nav click or a bad id opened the
 *     newest run's document without a word. Both screens now render THIS when the URL names
 *     a run they do not hold — which is the only honest thing to draw when nothing is open.
 *
 *  2. THE ONE IN HERE. It drew a FAKE DOCUMENT — grey skeleton lines, a fake toolbar, fake
 *     "HTML / PDF / QA" format chips — under a "Workspace ready" kicker. An empty state that
 *     draws a pretend document is lying about the one thing the product is for. It is gone.
 *     What stands in its place is true and it is useful: the three stages every run has, and
 *     what each one is for. The operator who lands here learns the shape of the product
 *     instead of looking at a mock-up of a document that does not exist.
 *
 * It offers the two next steps that actually exist — start a run, or open one from the
 * Library — and nothing else.
 */

const STAGES: Array<{ id: StageId; label: string }> = [
  { id: "draft", label: "Draft" },
  { id: "review", label: "Review" },
  { id: "package", label: "Package" }
];

const COPY: Record<StageId, { title: string; message: string }> = {
  draft: {
    title: "No run is open",
    message:
      "The draft belongs to a run. ATLAS will not pick one for you — start a run, or open one from the Library."
  },
  review: {
    title: "No run is open",
    message:
      "The review queue belongs to a run. ATLAS will not pick one for you — start a run, or open one from the Library."
  },
  package: {
    title: "No run is open",
    message: "Packaging belongs to a run. Start a run, or open one from the Library."
  }
};

export function EmptyRunState({ stage }: { stage: StageId }) {
  const copy = COPY[stage];
  return (
    <section className="empty-run-state" role="status" data-testid="empty-state">
      <div className="empty-run-copy">
        <span className="empty-state-mark" aria-hidden="true">
          <img src="/meridian-logo.svg" alt="" />
        </span>
        <h2>{copy.title}</h2>
        <p>{copy.message}</p>
        <div className="empty-run-actions">
          <Link className="btn btn-primary btn-lg btn-block" to="/new" data-testid="empty-start-run">
            <Plus aria-hidden="true" />
            Start a run
          </Link>
          <Link className="btn btn-secondary btn-lg btn-block" to="/runs" data-testid="empty-open-library">
            <LayoutDashboard aria-hidden="true" />
            Open the Library
          </Link>
        </div>
      </div>

      {/* THE SHAPE OF A RUN. Not a preview of a document that does not exist — the three
          stages that DO exist, in order, with what each one is for. The same three the nav
          shows once a run is open, in the same words. */}
      <div className="empty-run-visual">
        <p className="empty-run-flow-head">Every run has three stages</p>
        <div className="empty-assurance-list" aria-label="The stages of a run">
          {STAGES.map((item, index) => (
            <div key={item.id} data-current={item.id === stage ? "true" : "false"}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <strong>{item.label}</strong>
              <small>{STAGE_PURPOSE[item.id]}</small>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
